import { NativeNavigation, type StateObject } from "@eventr/native/navigation";
import { matchPath } from "react-router-dom";
import { allRouteNodes } from "routing/nodes";
import { isSheetPresentation, pathnameOf } from "routing/route";
import { presentedModalRootId, presentRouteSheet } from "./init";
import { nextEntrySheetStackId } from "./split.store";
import type { PushPathState } from "feat/globalRouter/performPush";
import type { TabId } from "feat/layout/tab-bar/tabs";
import { isTabOnBar, pushInTab, selectTab } from "./NativeRootController";
import { isIosNativeNavigation } from "./platform";
import { splitEntryTab } from "./split-entries";

/**
 * Work that has to happen before pushing to a route that presents as a sheet, so a plain
 * `pushPath` is all a caller needs: select the destination's tab while the old sheet is
 * still up, and tell the caller which sheet to dismiss, since the router presents a
 * differing modal config on top rather than swapping.
 *
 * Returns undefined when this is not a sheet navigation.
 */
export async function prepareSheetNavigation(path: string): Promise<{ dismiss: string | undefined } | undefined> {
  if (!isIosNativeNavigation()) return undefined;

  const node = allRouteNodes.find((route) => matchPath(route.path, pathnameOf(path)));
  if (!node || !isSheetPresentation(node.meta.present)) return undefined;
  // Opens in its tab's trailing split column, not as a sheet.
  if (splitEntryTab(path)) return undefined;

  if (node.meta.tab) {
    await selectTab(node.meta.tab);
  }

  return { dismiss: await liveModalRootId() };
}

/**
 * Opens `path`, an entry of `tab`'s list, in that tab's trailing split column from anywhere
 * else: another tab, or a sheet, which is dismissed first. Returns false when there is no tab
 * bar to open it in, for the caller to push as usual.
 */
export async function openSplitEntry(tab: TabId, path: string): Promise<boolean> {
  if (!isTabOnBar(tab)) return false;
  const sheet = await liveModalRootId();
  if (sheet) await NativeNavigation.dismiss({ id: sheet }).catch(() => {});
  // The screen sets its own title once it is up.
  return pushInTab(tab, path, "");
}

/**
 * Opens `path`, an entry of a pane tab's list, as a sheet over the screen it is opened from,
 * replacing any sheet already up. Closing it goes back to that screen. Returns false when the
 * route has no sheet, for the caller to push as usual.
 */
export async function presentEntrySheet(path: string, state?: PushPathState): Promise<boolean> {
  const sheet = await liveModalRootId();
  if (sheet) await NativeNavigation.dismiss({ id: sheet }).catch(() => {});
  // Router state is serialisable by contract, as the router assumes when it presents a sheet.
  return presentRouteSheet(path, nextEntrySheetStackId(), state as StateObject | undefined);
}

/**
 * The sheet to dismiss, by id. A dismiss without one takes whatever root is on top, and a
 * sheet that is already closing still lists here until its webview goes, so a bare dismiss
 * can land on the tabs root and leave a blank screen. The native check drops a sheet that
 * has already gone, since the plugin rejects a dismiss of an unknown id and the router
 * rethrows.
 */
async function liveModalRootId(): Promise<string | undefined> {
  const id = presentedModalRootId();
  if (!id) return undefined;
  try {
    await NativeNavigation.get({ id });
    return id;
  } catch {
    return undefined;
  }
}
